/**
 * Dish pointing for procedural antennas: the compass to asset-frame
 * conversion and the angles of the generator's drive nodes.
 *
 * Frames. The twin measures azimuth from +Z towards +X about +Y (fit.ts,
 * Lighting, the antenna generator), which is counter-clockwise seen from
 * above. A compass bearing is clockwise from true north. An asset's
 * `site.headingDeg` is the compass bearing of its +Z axis; without one it is
 * DEFAULT_HEADING_DEG (0: +Z is north, +X is west, right-handed with Y up).
 * A compass bearing B therefore maps to the asset azimuth `headingDeg - B`.
 *
 * Nodes. The antenna generator turns `drive_system` about Y for azimuth
 * (rotation.y = asset azimuth) and `el_pivot` about X for elevation
 * (rotation.x = -elevation), with the boresight on +Z at zero; the optical
 * terminal names its azimuth node `mount` with the same convention. A model
 * whose azimuth node has no `el_pivot` beneath it (GLB assets, other
 * generators) is not pointed.
 *
 * No three.js at runtime (types only), so the dive can import the conversion
 * without pulling the renderer into its chunk.
 */
import type { Object3D } from 'three';
import { easeInOutCubic } from './fit';

/** Compass bearing of an asset's +Z axis when its spec has no site.headingDeg. */
export const DEFAULT_HEADING_DEG = 0;
/** Pointing changes ease over this long (PLAN 4.3: eased, nothing bounces). */
export const POINTING_EASE_MS = 1000;
/** Azimuth node names, in lookup order: the antenna generator, then the optical terminal. */
export const AZIMUTH_NODES = ['drive_system', 'mount'] as const;
/** Elevation node name in both generators. */
export const ELEVATION_NODE = 'el_pivot';

const DEG = Math.PI / 180;

/** Compass azimuth (clockwise from true north) and elevation above the horizon, degrees. */
export interface CompassPointing {
  azimuthDeg: number;
  elevationDeg: number;
}

/** Drive angles in the asset frame: azimuth from +Z towards +X, elevation above the horizon, degrees. */
export interface DriveAngles {
  azimuthDeg: number;
  elevationDeg: number;
}

export function normaliseDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** Asset-frame azimuth of a compass bearing for an asset whose +Z axis points at `headingDeg`. */
export function compassToAssetAzimuth(bearingDeg: number, headingDeg: number = DEFAULT_HEADING_DEG): number {
  return normaliseDeg(headingDeg - bearingDeg);
}

/** Compass bearing of an asset-frame azimuth (the mapping is its own inverse). */
export function assetToCompassAzimuth(assetAzimuthDeg: number, headingDeg: number = DEFAULT_HEADING_DEG): number {
  return normaliseDeg(headingDeg - assetAzimuthDeg);
}

/** Drive angles for a compass pointing; elevation is held between the horizon and the zenith. */
export function driveAnglesFor(pointing: CompassPointing, headingDeg: number = DEFAULT_HEADING_DEG): DriveAngles {
  if (!Number.isFinite(pointing.azimuthDeg) || !Number.isFinite(pointing.elevationDeg) || !Number.isFinite(headingDeg)) {
    throw new Error(
      `driveAnglesFor: non-finite pointing (az ${pointing.azimuthDeg}, el ${pointing.elevationDeg}, heading ${headingDeg})`,
    );
  }
  return {
    azimuthDeg: compassToAssetAzimuth(pointing.azimuthDeg, headingDeg),
    elevationDeg: Math.max(0, Math.min(90, pointing.elevationDeg)),
  };
}

/** Signed shortest turn from `fromDeg` to `toDeg`, in -180..180. */
export function shortestTurnDeg(fromDeg: number, toDeg: number): number {
  const d = normaliseDeg(toDeg - fromDeg);
  return d > 180 ? d - 360 : d;
}

/** Angles between `from` and `to` at t (0..1, already eased): azimuth the short way round. */
export function lerpDriveAngles(from: DriveAngles, to: DriveAngles, t: number): DriveAngles {
  const k = Math.max(0, Math.min(1, t));
  return {
    azimuthDeg: normaliseDeg(from.azimuthDeg + shortestTurnDeg(from.azimuthDeg, to.azimuthDeg) * k),
    elevationDeg: from.elevationDeg + (to.elevationDeg - from.elevationDeg) * k,
  };
}

export interface DriveNodes {
  azimuth: Object3D;
  elevation: Object3D;
}

function isAncestor(candidate: Object3D, node: Object3D): boolean {
  let p = node.parent;
  while (p) {
    if (p === candidate) return true;
    p = p.parent;
  }
  return false;
}

/** The azimuth node and the el_pivot beneath it, or null when the model has no such pair. */
export function findDriveNodes(root: Object3D): DriveNodes | null {
  const byName = new Map<string, Object3D[]>();
  root.traverse((o) => {
    if (!o.name) return;
    const list = byName.get(o.name);
    if (list) list.push(o);
    else byName.set(o.name, [o]);
  });
  const pivots = byName.get(ELEVATION_NODE) ?? [];
  for (const name of AZIMUTH_NODES) {
    for (const azimuth of byName.get(name) ?? []) {
      const elevation = pivots.find((p) => isAncestor(azimuth, p));
      if (elevation) return { azimuth, elevation };
    }
  }
  return null;
}

export function readDriveAngles(nodes: DriveNodes): DriveAngles {
  return {
    azimuthDeg: normaliseDeg(nodes.azimuth.rotation.y / DEG),
    elevationDeg: -nodes.elevation.rotation.x / DEG,
  };
}

export function writeDriveAngles(nodes: DriveNodes, angles: DriveAngles): void {
  nodes.azimuth.rotation.y = angles.azimuthDeg * DEG;
  nodes.elevation.rotation.x = -angles.elevationDeg * DEG;
}

/**
 * Turns a model's drive nodes: `snap` jumps (a freshly mounted model, reduced
 * motion), `start` begins an eased move from the current angles and `step`
 * advances it; `step` returns true while the move is still running.
 */
export class DishPointingAnimator {
  private from: DriveAngles | null = null;
  private to: DriveAngles | null = null;
  private startMs = 0;
  private durationMs = POINTING_EASE_MS;
  /** True once any pointing has been applied to these nodes. */
  applied = false;

  constructor(readonly nodes: DriveNodes) {}

  get animating(): boolean {
    return this.to !== null;
  }

  snap(target: DriveAngles): void {
    writeDriveAngles(this.nodes, target);
    this.from = null;
    this.to = null;
    this.applied = true;
  }

  start(target: DriveAngles, nowMs: number, durationMs: number = POINTING_EASE_MS): void {
    if (durationMs <= 0) {
      this.snap(target);
      return;
    }
    this.from = readDriveAngles(this.nodes);
    this.to = target;
    this.startMs = nowMs;
    this.durationMs = durationMs;
    this.applied = true;
  }

  step(nowMs: number): boolean {
    if (!this.to || !this.from) return false;
    const t = Math.min(1, Math.max(0, (nowMs - this.startMs) / this.durationMs));
    writeDriveAngles(this.nodes, lerpDriveAngles(this.from, this.to, easeInOutCubic(t)));
    if (t >= 1) {
      this.from = null;
      this.to = null;
      return false;
    }
    return true;
  }
}
